'use client';

import { usePathname } from 'next/navigation';
import { createContext, useContext, useEffect, useState, useMemo, useRef, type ReactNode } from 'react';

/** Draft status for workflow */
export type DocumentDraftStatus = 'new' | 'draft' | 'marked_for_review' | 'validated';

/** Action bar state for the navbar - callbacks are stored in refs to avoid re-renders */
export interface ActionBarState {
  isEditMode: boolean;
  hasDraft: boolean;
  isViewingDraft: boolean;
  draftStatus?: DocumentDraftStatus;
  canEdit: boolean;
  canComment: boolean;
  isSaveDisabled: boolean;
  isSaving: boolean;
  rightPanelMode: 'outline' | 'comments' | null;
  /** Transient UI feedback for copy actions (syncs in-page + sticky nav action bars) */
  copiedMarkdown: boolean;
  copiedAgentReference: boolean;
  /** Loading state for PDF download */
  isDownloadingPdf: boolean;
  /** Loading state for DOCX download */
  isDownloadingDocx: boolean;
}

/** Action bar callbacks */
export interface ActionBarCallbacks {
  onEditModeChange?: (isEdit: boolean) => void;
  onRightPanelModeChange?: (mode: 'outline' | 'comments' | null) => void;
  onBack?: () => void;
  onForward?: () => void;
  onRefresh?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onSave?: () => void;
  onSaveAndPublish?: () => void;
  onPublish?: () => void;
  onStatusChange?: (status: DocumentDraftStatus) => void;
  onCopyMarkdown?: () => void;
  onDownloadPdf?: () => void;
  onDownloadDocx?: () => void;
  onCopyAgentReference?: () => void;
}

interface NavScrollContextValue {
  /** Whether the document header has scrolled past the navbar (Step 1 - triggers breadcrumb + menu injection) */
  headerHidden: boolean;
  /** Set the header hidden state */
  setHeaderHidden: (hidden: boolean) => void;
  /** Whether the action bar has scrolled past the navbar (Step 2 - triggers action bar in nav) */
  actionBarHidden: boolean;
  /** Set the action bar hidden state */
  setActionBarHidden: (hidden: boolean) => void;
  /** @deprecated Use headerHidden instead - kept for backwards compatibility */
  headerScrolledPast: boolean;
  /** @deprecated Use setHeaderHidden instead - kept for backwards compatibility */
  setHeaderScrolledPast: (scrolled: boolean) => void;
  /** Current document title for the navbar breadcrumb */
  documentTitle: string;
  /** Set the document title */
  setDocumentTitle: (title: string) => void;
  /** Current vault name for the navbar breadcrumb */
  vaultName: string;
  /** Set the vault name */
  setVaultName: (name: string) => void;
  /** Current document version for the navbar breadcrumb ('draft' or commit hash) */
  documentVersion: string;
  /** Set the document version */
  setDocumentVersion: (version: string) => void;
  /** Current document version status for coloring the breadcrumb version */
  documentVersionStatus: DocumentDraftStatus | null;
  /** Set the document version status */
  setDocumentVersionStatus: (status: DocumentDraftStatus | null) => void;
  /** Action bar state for navbar display */
  actionBarState: ActionBarState | null;
  /** Set action bar state */
  setActionBarState: (state: ActionBarState | null) => void;
  /** Action bar callbacks ref */
  actionBarCallbacksRef: React.MutableRefObject<ActionBarCallbacks>;
}

const NavScrollContext = createContext<NavScrollContextValue | null>(null);

interface NavScrollProviderProps {
  children: ReactNode;
}

export function NavScrollProvider({ children }: NavScrollProviderProps) {
  const pathname = usePathname() ?? '';
  // Two-step scroll states
  const [headerHidden, setHeaderHidden] = useState(false);
  const [actionBarHidden, setActionBarHidden] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [vaultName, setVaultName] = useState('');
  const [documentVersion, setDocumentVersion] = useState('');
  const [documentVersionStatus, setDocumentVersionStatus] = useState<DocumentDraftStatus | null>(null);
  const [actionBarState, setActionBarState] = useState<ActionBarState | null>(null);
  const actionBarCallbacksRef = useRef<ActionBarCallbacks>({});
  const previousPathnameRef = useRef<string | null>(null);

  // Reset navigation UI state when route changes to avoid stale breadcrumb/menu flashes.
  useEffect(() => {
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = pathname;
      return;
    }
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;

    setHeaderHidden(false);
    setActionBarHidden(false);
    setDocumentTitle('');
    setVaultName('');
    setDocumentVersion('');
    setDocumentVersionStatus(null);
    setActionBarState(null);
    actionBarCallbacksRef.current = {};
  }, [pathname]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo<NavScrollContextValue>(
    () => ({
      // New two-step states
      headerHidden,
      setHeaderHidden,
      actionBarHidden,
      setActionBarHidden,
      // Backwards compatibility - headerScrolledPast now equals actionBarHidden
      headerScrolledPast: actionBarHidden,
      setHeaderScrolledPast: setActionBarHidden,
      // Other state
      documentTitle,
      setDocumentTitle,
      vaultName,
      setVaultName,
      documentVersion,
      setDocumentVersion,
      documentVersionStatus,
      setDocumentVersionStatus,
      actionBarState,
      setActionBarState,
      actionBarCallbacksRef,
    }),
    [
      headerHidden,
      actionBarHidden,
      documentTitle,
      vaultName,
      documentVersion,
      documentVersionStatus,
      actionBarState,
    ]
  );

  return (
    <NavScrollContext.Provider value={contextValue}>
      {children}
    </NavScrollContext.Provider>
  );
}

export function useNavScroll() {
  const context = useContext(NavScrollContext);
  if (!context) {
    throw new Error('useNavScroll must be used within a NavScrollProvider');
  }
  return context;
}

/** Optional hook that returns null if outside provider (for components that may render outside vault context) */
export function useNavScrollOptional() {
  return useContext(NavScrollContext);
}

export default NavScrollContext;
